import { useCallback, useEffect, useRef, useState } from 'react';

// Declare window.IGLOO type
declare global {
  interface Window {
    IGLOO?: {
      getBlackbox?: () => {
        finished: boolean;
        blackbox: string;
      };
      trace_handler?: (message: string) => void;
    };
  }
}

/**
 * Custom hook to handle iovation fingerprinting
 * @returns Object with initialization function and ready state
 */
export const useIovationFingerprint = () => {
  const [fingerprintReady, setFingerprintReady] = useState(false);
  const fingerprintSent = useRef(false);

  // Send fingerprint to backend
  const sendFingerprintToBackend = async (fingerprint: string) => {
    if (fingerprintSent.current) return;

    try {
      const response = await fetch(
        '/app_api/iovation/set_iovation_fingerprint',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fingerprint }),
          credentials: 'include', // Important for cookies
        }
      );

      const data = await response.json();

      if (response.ok) {
        console.log('iovation fingerprint stored successfully:', data);
        fingerprintSent.current = true;
        setFingerprintReady(true);
      } else {
        console.error('Failed to store iovation fingerprint:', data);
      }
    } catch (error) {
      console.error('Error sending iovation fingerprint to backend:', error);
    }
  };

  // Generate iovation fingerprint with Fibonacci-like backoff
  const generateFingerprint = () => {
    let previousDelay = 0;
    let currentDelay = 10;
    const maxTotalTime = 10000; // 10 seconds
    let elapsedTime = 0;

    const getNextDelay = () => {
      const nextDelay = previousDelay + currentDelay;
      previousDelay = currentDelay;
      currentDelay = Math.min(nextDelay, 1000); // Cap individual delay at 1 second
      return currentDelay;
    };

    const attemptGeneration = () => {
      console.log('Checking iovation... window.IGLOO:', window.IGLOO);

      if (!window.IGLOO) {
        console.log('window.IGLOO not defined yet');
        if (elapsedTime < maxTotalTime) {
          const delay = getNextDelay();
          elapsedTime += delay;
          setTimeout(attemptGeneration, delay);
        } else {
          console.error('iovation failed to load after max time');
        }
        return;
      }

      if (typeof window.IGLOO.getBlackbox !== 'function') {
        console.log('window.IGLOO.getBlackbox not a function yet, retrying...');
        if (elapsedTime < maxTotalTime) {
          const delay = getNextDelay();
          elapsedTime += delay;
          setTimeout(attemptGeneration, delay);
        } else {
          console.error(
            'iovation getBlackbox failed to initialize after max time'
          );
        }
        return;
      }

      const bbData = window.IGLOO.getBlackbox();
      console.log('iovation bbData:', bbData);

      if (bbData.finished) {
        const blackBoxString = bbData.blackbox;
        const encodedBlackBox = encodeURIComponent(blackBoxString);
        console.log('iovation fingerprint (raw):', blackBoxString);
        console.log('iovation fingerprint (encoded):', encodedBlackBox);

        // Send fingerprint to backend to store in cookie
        sendFingerprintToBackend(blackBoxString);
      } else {
        console.log('iovation fingerprint not ready yet, retrying...');
        if (elapsedTime < maxTotalTime) {
          const delay = getNextDelay();
          elapsedTime += delay;
          setTimeout(attemptGeneration, delay);
        } else {
          console.log(
            'iovation fingerprint not finished after max time, using unfinished value'
          );
          console.log('iovation fingerprint (unfinished):', bbData.blackbox);

          // Send fingerprint even if not fully finished
          sendFingerprintToBackend(bbData.blackbox);
        }
      }
    };

    // Try to generate fingerprint, with retry logic
    setTimeout(attemptGeneration, 10);
  };

  /**
   * Initialize iovation fingerprinting
   * Call this function when you want to start the fingerprinting process
   */
  const initializeIovation = useCallback(() => {
    if (fingerprintSent.current) {
      console.log('iovation fingerprint already sent, skipping');
      return;
    }

    console.log('Initializing iovation fingerprinting');
    window.IGLOO = window.IGLOO || {};
    window.IGLOO.trace_handler = function (message) {
      console.log('iovation trace:', message);
    };

    generateFingerprint();
  }, []);

  return {
    initializeIovation,
    fingerprintReady,
  };
};
